import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mail, 
  TrendingUp, 
  ShieldCheck, 
  Users, 
  Calendar,
  ChevronDown,
  Mountain,
  Target,
  Award,
  CheckCircle,
  X,
  Loader2,
  ArrowRight,
  ArrowLeft,
  MapPin,
  DollarSign,
  Stethoscope,
  ExternalLink,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ChatWidget from "@/ChatWidget";
import { SubstackHeaderButton, SubstackFooterLink, SubstackInlineCTA } from "@/components/SubstackLink";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Custom Ascent Peak Icon - upward only, no descent
const AscentIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21 12 3l4 8" />
    <path d="M12 3l9 18H3" />
  </svg>
);

// Ridgemont Logo Icon - ascending peak with single bar
const RidgemontLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 32 34" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 28 16 4 28 28" />
    <path d="M9 28 16 14 23 28" />
    <line x1="3" y1="32" x2="29" y2="32" strokeWidth="2" />
  </svg>
);

// Logo URL
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/96f08e1b-7980-446d-91e9-995f5c811979/images/9f8173b33070cd48eac1609400983c8b47e2e2e28545a37660d6d24a2d5ade82.png";

// Hero background - Troon North Golf Course, Scottsdale
const HERO_BG = "https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/9xt4hwqb_Troon%20North.jpg";

// Lifestyle image - businessmen handshake
const LIFESTYLE_IMG = "https://images.unsplash.com/photo-1758599543278-32d9d073941e?w=800&q=80";

// LeadConnector booking widget URL
const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/FsGuR37Niq0fFY5F2tii";

// Free Book Lead Magnet Image
const BOOK_IMAGE = "https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/x50ge65l_IMG_2743.webp";

// Specialty options for physicians
const SPECIALTY_OPTIONS = [
  "Anesthesiology",
  "Cardiology",
  "Dermatology",
  "Emergency Medicine",
  "Endocrinology",
  "Family Medicine",
  "Gastroenterology",
  "General Surgery",
  "Internal Medicine",
  "Neurology",
  "Obstetrics & Gynecology",
  "Oncology",
  "Ophthalmology",
  "Orthopedic Surgery",
  "Pathology",
  "Pediatrics",
  "Plastic Surgery",
  "Psychiatry",
  "Radiology",
  "Urology",
  "Engineering",
  "Attorney / Law",
  "Other"
];




// Shared form submission logic
const useLeadForm = (onSuccess) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    phone: '',
    email: '',
    annual_income: '',
    zip_code: '',
    specialty: '',
    preferred_contact: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.age || formData.age < 25 || formData.age > 75) newErrors.age = "Age must be between 25-75";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";
    if (!formData.annual_income.trim()) newErrors.annual_income = "Annual income is required";
    if (!formData.zip_code.trim()) newErrors.zip_code = "Zip code is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, source) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/leads/kaizen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          age: parseInt(formData.age),
          phone: formData.phone,
          email: formData.email,
          annual_income: formData.annual_income,
          zip_code: formData.zip_code,
          specialty: formData.specialty,
          preferred_contact: formData.preferred_contact || 'phone',
          source: source || 'kaizen_calculator'
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (onSuccess) onSuccess();
        setFormData({ name: '', age: '', phone: '', email: '', annual_income: '', zip_code: '', specialty: '', preferred_contact: '' });
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      if (onSuccess) onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const setField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return { formData, errors, isSubmitting, handleSubmit, handleChange, setField };
};

// Lead Capture Modal Component
const LeadCaptureModal = ({ isOpen, onClose }) => {
  const { formData, errors, isSubmitting, handleSubmit, handleChange, setField } = useLeadForm(onClose);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <motion.div
          className="relative bg-white w-full max-w-lg p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-[#57534E] hover:text-[#0A192F] transition-colors"
            data-testid="modal-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-[#1B4D3E] flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-6 h-6 text-[#C5A059]" />
            </div>
            <h2 className="text-2xl font-light text-[#0A192F] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Run Your Numbers
            </h2>
            <p className="text-sm text-[#57534E]">
              Enter your details to see your personalized Kai-Zen® illustration
            </p>
          </div>
          
          {/* Form */}
          <form onSubmit={(e) => handleSubmit(e, 'kaizen_modal')} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="modal-name" className="text-[#0A192F]">Full Name *</Label>
                <Input
                  id="modal-name"
                  name="name"
                  type="text"
                  placeholder="Dr. John Smith"
                  value={formData.name}
                  onChange={handleChange}
                  className={`mt-1 ${errors.name ? 'border-red-500' : ''}`}
                  data-testid="lead-name-input"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <Label htmlFor="modal-age" className="text-[#0A192F]">Age *</Label>
                <Input
                  id="modal-age"
                  name="age"
                  type="number"
                  placeholder="45"
                  min="25"
                  max="75"
                  value={formData.age}
                  onChange={handleChange}
                  className={`mt-1 ${errors.age ? 'border-red-500' : ''}`}
                  data-testid="lead-age-input"
                />
                {errors.age && <p className="text-red-500 text-xs mt-1">{errors.age}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="modal-phone" className="text-[#0A192F]">Phone Number *</Label>
                <Input
                  id="modal-phone"
                  name="phone"
                  type="tel"
                  placeholder="(480) 555-1234"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                  data-testid="lead-phone-input"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
              
              <div>
                <Label htmlFor="modal-email" className="text-[#0A192F]">Email Address *</Label>
                <Input
                  id="modal-email"
                  name="email"
                  type="email"
                  placeholder="john@hospital.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 ${errors.email ? 'border-red-500' : ''}`}
                  data-testid="lead-email-input"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="modal-income" className="text-[#0A192F]">Annual Income *</Label>
                <Input
                  id="modal-income"
                  name="annual_income"
                  type="text"
                  placeholder="$350,000"
                  value={formData.annual_income}
                  onChange={handleChange}
                  className={`mt-1 ${errors.annual_income ? 'border-red-500' : ''}`}
                  data-testid="lead-income-input"
                />
                {errors.annual_income && <p className="text-red-500 text-xs mt-1">{errors.annual_income}</p>}
              </div>
              
              <div>
                <Label htmlFor="modal-zip" className="text-[#0A192F]">Zip Code *</Label>
                <Input
                  id="modal-zip"
                  name="zip_code"
                  type="text"
                  placeholder="85251"
                  maxLength={10}
                  value={formData.zip_code}
                  onChange={handleChange}
                  className={`mt-1 ${errors.zip_code ? 'border-red-500' : ''}`}
                  data-testid="lead-zip-input"
                />
                {errors.zip_code && <p className="text-red-500 text-xs mt-1">{errors.zip_code}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="modal-specialty" className="text-[#0A192F]">Specialty</Label>
              <Select value={formData.specialty} onValueChange={(val) => setField('specialty', val)}>
                <SelectTrigger className="mt-1" data-testid="lead-specialty-select">
                  <SelectValue placeholder="Select your specialty" />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALTY_OPTIONS.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#0A192F] mb-2 block">Preferred Contact Method</Label>
              <div className="flex gap-4">
                {['Phone', 'Email', 'Text'].map((method) => (
                  <label key={method} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preferred_contact"
                      value={method.toLowerCase()}
                      checked={formData.preferred_contact === method.toLowerCase()}
                      onChange={handleChange}
                      className="w-4 h-4 accent-[#C5A059]"
                      data-testid={`lead-contact-${method.toLowerCase()}`}
                    />
                    <span className="text-sm text-[#0A192F]">{method}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 py-6 text-lg font-semibold"
              data-testid="lead-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5 mr-2" />
                  See My Numbers
                </>
              )}
            </Button>
          </form>
          
          <p className="text-xs text-[#57534E] text-center mt-4">
            Your information is secure and will only be used to provide your personalized illustration.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const stagger = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  transition: { staggerChildren: 0.2 }
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.nav 
      data-testid="navigation"
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-glass shadow-md top-0" : "bg-transparent top-0"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Text-based logo for cleaner look */}
          <div className="flex items-center gap-3" data-testid="logo">
            <RidgemontLogo className={`w-8 h-8 ${scrolled ? "text-[#1B4D3E]" : "text-[#C5A059]"}`} />
            <div className="flex flex-col">
              <span className={`text-xl md:text-2xl font-light tracking-tight ${scrolled ? "text-[#0A192F]" : "text-white"}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ridgemont
              </span>
              <span className={`text-[10px] md:text-xs tracking-[0.25em] uppercase ${scrolled ? "text-[#1B4D3E]" : "text-[#C5A059]"}`}>
                Financial
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to="/team"
              className={`hidden sm:inline text-sm font-medium tracking-wide transition-colors ${scrolled ? "text-[#0A192F] hover:text-[#C5A059]" : "text-white hover:text-[#C5A059]"}`}
              data-testid="nav-team-link"
            >
              Our Team
            </Link>
            <SubstackHeaderButton />
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="btn-accent px-5 py-2 md:px-6 md:py-5 text-sm md:text-base"
                data-testid="nav-cta-button"
              >
                Schedule Call
              </Button>
            </a>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section data-testid="hero-section" className="hero-section">
      <div className="hero-bg">
        <img src={HERO_BG} alt="Troon North Golf Course" />
        <div className="hero-overlay" />
      </div>
      
      <div className="hero-content max-w-[1400px] mx-auto px-4 md:px-8 pt-32 pb-20">
        <motion.div 
          className="max-w-2xl bg-[#0A192F]/75 backdrop-blur-sm p-10 md:p-14"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <motion.p 
            className="text-sm md:text-base tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Ridgemont Financial
          </motion.p>
          
          <h1 
            className="text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-[1.1] tracking-tight mb-6"
            data-testid="hero-headline"
          >
            Your Retirement Paycheck,
            <span className="block text-[#C5A059]">Planned & Protected.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-10 max-w-2xl">
            Stop guessing what retirement looks like. Talk with a Ridgemont advisor
            and see what kind of monthly income strategy could fit your retirement.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={() => scrollToSection("booking")}
              className="btn-accent px-8 py-6 text-lg"
              data-testid="hero-cta-button"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>
        
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <ChevronDown className="w-8 h-8 text-white/50 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
// Services Section - How It Works + Income Comparison
const ServicesSection = () => {
  const steps = [
    { number: "01", title: "Schedule a Call", description: "Book a free, no-pressure conversation with a Ridgemont retirement specialist." },
    { number: "02", title: "Share Your Goals", description: "Walk us through your savings, income needs, and retirement timeline." },
    { number: "03", title: "Get Your Plan", description: "We design a personalized income strategy built around protection and growth." },
    { number: "04", title: "Lock It In", description: "Put your plan into action and start building your retirement paycheck." }
  ];

  const comparisons = [
    { category: "Monthly Income", protected: "Designed to provide steady monthly payments for life*", traditional: "Fluctuates with market conditions" },
    { category: "Market Risk", protected: "Insulated from market downturns by contract", traditional: "Fully exposed — losses reduce income" },
    { category: "Longevity Risk", protected: "Payments continue as long as you live*", traditional: "Savings can run out" },
    { category: "Tax Treatment", protected: "Tax-deferred growth, favorable withdrawals", traditional: "Taxed on gains every year" },
    { category: "Management Required", protected: "Set it and forget it", traditional: "Constant rebalancing and monitoring" },
    { category: "Sleep at Night", protected: "Paycheck arrives like clockwork", traditional: "Stress every time the market drops" }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#FAFAF9]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* How It Works */}
        <motion.div 
          className="text-center mb-16 md:mb-20"
          {...fadeUp}
          viewport={{ once: true }}
        >
          <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
            How It Works
          </p>
          <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            From Question Marks to Certainty
          </h2>
          <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
            Four steps. One clear plan. A retirement income you can count on.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-20">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              className="bg-white p-8 border border-[#E7E5E4] relative text-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              data-testid={`step-card-${index}`}
            >
              <span className="text-6xl font-light text-[#C5A059]/20 absolute top-4 right-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {step.number}
              </span>
              <h3 className="text-lg font-medium text-[#0A192F] mb-3 mt-6">{step.title}</h3>
              <p className="text-sm text-[#57534E] leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
            The Difference
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Protected Income vs. Hope-for-the-Best
          </h2>
          <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
            See how a protected income strategy compares to relying solely on the market to fund your retirement.
          </p>
        </motion.div>

        <motion.div
          className="max-w-4xl mx-auto bg-white border border-[#E7E5E4] shadow-lg overflow-hidden"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          data-testid="comparison-table"
        >
          <div className="grid grid-cols-3 bg-[#0A192F]">
            <div className="p-4 text-white/80 text-sm font-medium">Category</div>
            <div className="p-4 text-[#C5A059] text-sm font-medium text-center">Protected Income</div>
            <div className="p-4 text-white/60 text-sm font-medium text-center">Market-Dependent</div>
          </div>
          {comparisons.map((row, index) => (
            <div key={index} className="grid grid-cols-3 border-t border-[#E7E5E4]">
              <div className="p-4 text-[#0A192F] text-sm font-medium">{row.category}</div>
              <div className="p-4 text-sm text-center">
                <span className="flex items-center justify-center gap-2 text-[#1B4D3E]">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{row.protected}</span>
                </span>
              </div>
              <div className="p-4 text-sm text-center text-[#57534E]">
                <span className="flex items-center justify-center gap-2 text-red-400">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span className="text-[#57534E]">{row.traditional}</span>
                </span>
              </div>
            </div>
          ))}
        </motion.div>
        <p className="text-xs text-[#57534E]/70 mt-4 max-w-4xl mx-auto text-center italic">
          *Lifetime income features are contractual provisions of fixed and fixed-index annuities, subject to the financial strength and claims-paying ability of the issuing insurance company. Product features, riders, and limitations vary by carrier and contract.
        </p>
      </div>
    </section>
  );
};

// US State options
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

// Premium Bonus Promo Section
const BonusPromoSection = () => (
  <section data-testid="bonus-promo-section" className="py-24 md:py-32 bg-[#1B4D3E]">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
            Limited Time
          </p>
          <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Up to <span className="text-[#C5A059]">52%</span> Premium Bonus
            <span className="block text-[#C5A059]">on Select Annuities</span>
          </h2>
          <p className="text-lg text-white/70 leading-relaxed mb-8">
            Top carriers like Allianz, Athene, and North American are offering their biggest premium bonuses of the year. 
            Your money gets a head start from day one — before the market even opens.
          </p>
          <a href="/retirement-annuity-premium-bonus">
            <Button className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-8 py-6 text-lg font-semibold" data-testid="bonus-promo-cta">
              <TrendingUp className="w-5 h-5 mr-2" />
              Compare Offers & Get a Proposal
            </Button>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-3 gap-4">
            {[
              { carrier: "Allianz", bonus: "52%", product: "222+" },
              { carrier: "Athene", bonus: "34%", product: "Performance Elite" },
              { carrier: "North American", bonus: "29%", product: "Charter Plus" }
            ].map((item, i) => (
              <motion.div key={i} className="bg-white/10 backdrop-blur-sm p-6 border border-white/20 text-center"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.15 }} viewport={{ once: true }}>
                <p className="text-xs tracking-wider text-white/40 uppercase mb-2">{item.carrier}</p>
                <p className="text-4xl font-light text-[#C5A059] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item.bonus}</p>
                <p className="text-white/50 text-xs">Premium Bonus</p>
                <p className="text-white/30 text-xs mt-2">{item.product}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-white/30 text-xs mt-4 text-center italic">
            Bonus amounts and availability subject to change. See product page for details.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);


// Free Book Lead Magnet Teaser — slim homepage section that drives to /free-book landing page
const FreeBookSection = () => (
  <section id="free-book" data-testid="free-book-section" className="py-20 md:py-28 bg-white">
    <div className="max-w-[1200px] mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="flex justify-center lg:justify-end"
        >
          <img
            src={BOOK_IMAGE}
            alt="Tax-Free Retirement by Patrick Kelly"
            className="w-full max-w-[280px] md:max-w-[320px] drop-shadow-2xl"
            style={{ transform: 'rotate(-2deg)' }}
            data-testid="free-book-teaser-image"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          viewport={{ once: true }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">
            Free Book &middot; We Cover Shipping
          </p>
          <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Tax-Free Retirement
            <span className="block text-[#C5A059] text-2xl md:text-3xl mt-1">by Patrick Kelly</span>
          </h2>
          <p className="text-lg text-[#57534E] leading-relaxed mb-6">
            National bestseller. Over 500,000 copies in print. We'll mail you a physical copy &mdash; no PDF, no upsell, no catch.
          </p>
          <ul className="space-y-2.5 mb-8">
            {[
              "The 9 financial landmines that quietly drain retirement income",
              "How to structure tax-free retirement distributions",
              "Strategies the IRS doesn't advertise"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[#0A192F] text-sm md:text-base">
                <CheckCircle className="w-5 h-5 text-[#1B4D3E] flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <a href="/free-book">
            <Button
              className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 px-8 py-6 text-lg font-semibold"
              data-testid="free-book-teaser-cta"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Claim My Free Copy
            </Button>
          </a>
          <p className="text-xs text-[#57534E]/70 mt-3">U.S. shipping only. Allow 5-7 business days.</p>
        </motion.div>
      </div>
    </div>
  </section>
);


// Inline Lead Capture Form Section
const InlineLeadSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const { formData, errors, isSubmitting, handleSubmit, handleChange, setField } = useLeadForm(() => setSubmitted(true));

  return (
    <section id="get-started" data-testid="inline-lead-section" className="py-24 md:py-32 bg-[#0A192F]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              Get Started Today
            </p>
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              See What Your
              <span className="block text-[#C5A059]">Retirement Could Look Like</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Whether you're exploring Kai-Zen® or want a comprehensive retirement income plan, 
              the first step is understanding your numbers. Fill out this quick form and 
              we'll show you what's possible.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-[#C5A059]" />
                </div>
                <p className="text-white/80 text-sm">No sales call required — run your own numbers first</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-[#C5A059]" />
                </div>
                <p className="text-white/80 text-sm">Personalized illustration based on your age and income</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-[#C5A059]" />
                </div>
                <p className="text-white/80 text-sm">Takes less than 2 minutes to complete</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {submitted ? (
              <div className="bg-[#1B4D3E] p-10 text-center">
                <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-4" />
                <h3 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Thank You!
                </h3>
                <p className="text-white/70">Your personalized illustration is opening in a new tab.</p>
              </div>
            ) : (
              <div className="bg-white p-8 md:p-10 shadow-2xl" data-testid="inline-lead-form">
                <h3 className="text-xl font-medium text-[#0A192F] mb-6">Get Your Free Illustration</h3>
                <form onSubmit={(e) => handleSubmit(e, 'inline_form')} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="inline-name" className="text-[#0A192F] text-sm">Full Name *</Label>
                      <Input id="inline-name" name="name" placeholder="Dr. John Smith" value={formData.name} onChange={handleChange} className={`mt-1 ${errors.name ? 'border-red-500' : ''}`} data-testid="inline-name-input" />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="inline-age" className="text-[#0A192F] text-sm">Age *</Label>
                      <Input id="inline-age" name="age" type="number" placeholder="45" min="25" max="75" value={formData.age} onChange={handleChange} className={`mt-1 ${errors.age ? 'border-red-500' : ''}`} data-testid="inline-age-input" />
                      {errors.age && <p className="text-red-500 text-xs mt-1">{errors.age}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="inline-phone" className="text-[#0A192F] text-sm">Phone *</Label>
                      <Input id="inline-phone" name="phone" type="tel" placeholder="(480) 555-1234" value={formData.phone} onChange={handleChange} className={`mt-1 ${errors.phone ? 'border-red-500' : ''}`} data-testid="inline-phone-input" />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <Label htmlFor="inline-email" className="text-[#0A192F] text-sm">Email *</Label>
                      <Input id="inline-email" name="email" type="email" placeholder="john@hospital.com" value={formData.email} onChange={handleChange} className={`mt-1 ${errors.email ? 'border-red-500' : ''}`} data-testid="inline-email-input" />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="inline-income" className="text-[#0A192F] text-sm">Annual Income *</Label>
                      <Input id="inline-income" name="annual_income" placeholder="$350,000" value={formData.annual_income} onChange={handleChange} className={`mt-1 ${errors.annual_income ? 'border-red-500' : ''}`} data-testid="inline-income-input" />
                      {errors.annual_income && <p className="text-red-500 text-xs mt-1">{errors.annual_income}</p>}
                    </div>
                    <div>
                      <Label htmlFor="inline-zip" className="text-[#0A192F] text-sm">Zip Code *</Label>
                      <Input id="inline-zip" name="zip_code" placeholder="85251" maxLength={10} value={formData.zip_code} onChange={handleChange} className={`mt-1 ${errors.zip_code ? 'border-red-500' : ''}`} data-testid="inline-zip-input" />
                      {errors.zip_code && <p className="text-red-500 text-xs mt-1">{errors.zip_code}</p>}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="inline-specialty" className="text-[#0A192F] text-sm">Specialty</Label>
                    <Select value={formData.specialty} onValueChange={(val) => setField('specialty', val)}>
                      <SelectTrigger className="mt-1" data-testid="inline-specialty-select">
                        <SelectValue placeholder="Select your specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIALTY_OPTIONS.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#0A192F] text-sm mb-2 block">Preferred Contact Method</Label>
                    <div className="flex gap-4">
                      {['Phone', 'Email', 'Text'].map((method) => (
                        <label key={method} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="preferred_contact" value={method.toLowerCase()} checked={formData.preferred_contact === method.toLowerCase()} onChange={handleChange} className="w-4 h-4 accent-[#C5A059]" data-testid={`inline-contact-${method.toLowerCase()}`} />
                          <span className="text-sm text-[#0A192F]">{method}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 py-6 text-lg font-semibold" data-testid="inline-submit-btn">
                    {isSubmitting ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>) : (<><TrendingUp className="w-5 h-5 mr-2" />Get My Free Illustration</>)}
                  </Button>
                </form>
                <p className="text-xs text-[#57534E] text-center mt-3">Your information is secure and will only be used to provide your personalized illustration.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Banner component
const CTABanner = () => (
  <section data-testid="cta-banner" className="py-16 bg-[#C5A059]">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h3 className="text-2xl md:text-3xl font-light text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Know Your Number Before You Retire
        </h3>
        <p className="text-[#0A192F]/70 mb-8 max-w-xl mx-auto">
          Most people have no idea how much protected retirement income they could qualify for. A short conversation with a Ridgemont advisor changes that.
        </p>
        <div className="flex justify-center">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 px-8 py-6 text-lg"
              data-testid="cta-schedule-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule a Call
            </Button>
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);


// Physician Section
const PhysicianSection = ({ onOpenLeadForm }) => {
  const benefits = [
    "3-to-1 leverage — bank matches your contributions to amplify capital at work",
    "Tax-free growth and tax-free retirement distributions",
    "0% floor protection — designed to participate in market gains while helping limit losses from downturns",
    "Enhanced death benefits for legacy planning",
    "Living benefits for chronic or terminal illness",
    "Frictionless financing — no credit checks, no personal guarantees, no out-of-pocket interest"
  ];

  return (
    <section data-testid="physician-section" className="py-24 md:py-32 bg-[#1B4D3E]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              Exclusive Physician Services
            </p>
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              $250K Over 5 Years.
              <span className="block text-[#C5A059]">$3M+ at 65.*</span>
            </h2>
            <p className="text-sm text-white/50 mb-6">*Based on a 40-year-old participant</p>
            <p className="text-lg text-white/80 leading-relaxed mb-4">
              Every retirement plan you've been shown assumed you'd contribute for 30 years. 
              Most physicians do exactly that — and still come up short.
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-4">
              Our doctor clients fund for just 5 years. A bank matches their contributions 3-to-1 
              and assumes the cost after that. No credit check. No personal guarantee. No out-of-pocket interest.
            </p>
            <p className="text-xl text-white font-medium mb-8">
              That's not a typo. That's <span className="text-[#C5A059]">Kai-Zen®</span>.
            </p>
            <Button 
              onClick={onOpenLeadForm}
              className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-8 py-6 text-lg font-semibold"
              data-testid="physician-cta-button"
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              Run Your Own Numbers
            </Button>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm p-8 md:p-10 border border-white/20"
          >
            <h3 className="text-2xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              The Kai-Zen® Advantage
            </h3>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <span className="text-white/90">{benefit}</span>
                </motion.li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-white/60 text-sm">
                Research shows 74% of financial success in retirement comes from how much capital is working for you — not your returns, not your fund selection. Kai-Zen® puts dramatically more capital to work.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Value Proposition Section
const ValueSection = () => {
  const values = [
    { icon: ShieldCheck, title: "Income Built to Last", description: "Strategies designed to deliver a steady monthly paycheck throughout retirement — structured around contractual lifetime income features." },
    { icon: Mountain, title: "Built-In Downside Protection", description: "Approaches that help shield your retirement savings from market volatility, so a downturn is less likely to derail your plan." },
    { icon: TrendingUp, title: "Tax-Smart Growth", description: "Keep more of what you earn with tax-deferred accumulation and favorable treatment when you start collecting." }
  ];

  return (
    <section data-testid="value-section" className="py-24 md:py-32 bg-[#0A192F]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
            The Ridgemont Approach
          </p>
          <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Retirement Should Feel
            <span className="block text-[#C5A059]">Like a Promotion, Not a Pay Cut</span>
          </h2>
          <p className="text-lg text-white/70 leading-relaxed mb-16 max-w-2xl mx-auto">
            With over 30 years of experience and <span className="text-[#C5A059] font-semibold">1,500+ clients</span> guided 
            to confident retirements, we help you build income that's predictable, protected, and designed to last.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-white/5 border border-white/10 p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                data-testid={`value-item-${index}`}
              >
                <div className="w-14 h-14 bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-[#C5A059]" />
                </div>
                <h4 className="text-white font-medium text-lg mb-3">{value.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Asset-Map Section
const AssetMapSection = () => {
  const benefits = [
    "One-page visual overview of your entire financial life",
    "See all assets, liabilities, insurance, and cash flow at a glance",
    "Identify gaps and opportunities in your financial plan",
    "Set specific goals with Target-Map functionality",
    "Create your personalized client portal"
  ];

  return (
    <section data-testid="assetmap-section" className="py-24 md:py-32 bg-[#FAFAF9]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              Complimentary for All Clients
            </p>
            <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              See Your Complete
              <span className="block text-[#1B4D3E]">Financial Picture</span>
            </h2>
            <p className="text-lg text-[#57534E] leading-relaxed mb-6">
              Every Ridgemont Financial client receives a personalized Asset-Map® — a powerful visual 
              planning tool that transforms complex financial data into a clear, one-page snapshot 
              of your entire financial life.
            </p>
            <p className="text-lg text-[#57534E] leading-relaxed mb-8">
              No more scattered statements or confusing spreadsheets. With Asset-Map, you'll see 
              exactly where you stand and what's missing — helping you make confident, informed decisions.
            </p>
            <a 
              href="https://app.asset-map.com/i/ZaPr3qjd"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 px-8 py-6 text-lg font-semibold">
                <Target className="w-5 h-5 mr-2" />
                Create Your Asset-Map
              </Button>
            </a>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 border border-[#E7E5E4] shadow-lg"
          >
            <div className="flex items-center gap-4 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/th20mriv_Asset-Map%20Logo.webp" 
                alt="Asset-Map" 
                className="h-12 w-auto"
              />
            </div>
            <h3 className="text-xl font-medium text-[#0A192F] mb-4">What You'll Get</h3>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <CheckCircle className="w-5 h-5 text-[#1B4D3E] mt-0.5 flex-shrink-0" />
                  <span className="text-[#57534E]">{benefit}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// LeadConnector Booking — opens the calendar in a new tab via direct link.
// All "Schedule Call" CTAs across the site route to BOOKING_URL directly.

// Team Section
const TeamSection = () => {
  const team = [
    {
      name: "Dr. Stepheny Finnie",
      slug: "stepheny-finnie",
      title: "Annuity Specialist",
      image: "https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/qfjmq886_DSC_0543.jpg",
      bio: "Dr. Finnie brings an advanced academic perspective to the real-world challenges retirees face today. She helps clients navigate the financial complexities of retirement with clarity and confidence."
    },
    {
      name: "Brandi Jo Newman",
      slug: "brandi-jo-newman",
      title: "Founder & CEO",
      image: "https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/1q2spafm_BJN.jpg",
      bio: "Thirty years inside the financial services industry — recruiting, scaling, and leading advisor teams of 70+ at the corporate level and 55 across Texas at the regional level. Brandi Jo built her career on process, precision, and a relentless focus on what actually helps pre-retirees protect what they've worked for. (A former LPGA Futures, Ladies European, and Asian Tour pro — but the golf is the footnote, not the headline.)",
      link: "https://brandijo.com"
    },
    {
      name: "Jeremy Dreesen, RICP®",
      slug: "jeremy-dreesen",
      title: "Retirement Income Specialist",
      image: "https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/qgoqz0cl_492743760_10227161341755460_4716790653305132222_n.jpg",
      bio: "Experienced life insurance agent and senior market specialist serving Northeast Texas, with a strong focus on retirement income planning. Jeremy helps clients create sustainable income strategies through Medicare optimization, life insurance solutions, and holistic financial planning — recognized for a client-first approach and practical, community-focused strategies that support long-term financial security."
    },
    {
      name: "Crawford James, CRPC®",
      slug: "crawford-james",
      title: "Retirement Income Specialist",
      image: "https://customer-assets-agu9un31.emergentagent.net/job_ridgemont-leads/artifacts/jzh09cw1_Merrill%20Pic.jpg",
      bio: "Crawford brings a wealth of financial services experience, technical expertise, and real-world perspective. A Chartered Retirement Planning Counselor (CRPC®) holding active Series 65, SIE, and Health Insurance licenses, he specializes in helping pre-retirees and retirees turn decades of hard-earned savings into predictable, tax-efficient retirement income. With a long history of advising working professionals, physicians, athletes, and retirees at all levels, Crawford builds customized, protected income strategies that shield portfolios from market volatility — bridging complex planning concepts into practical, actionable strategies so clients step into retirement with absolute confidence."
    }
  ];

  return (
    <section id="booking" data-testid="team-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
            Our Team
          </p>
          <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Meet Your Advisors
          </h2>
          <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
            Our team of specialists is here to guide you toward a confident retirement
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              className="text-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              data-testid={`team-member-${index}`}
            >
              <div className="relative mb-6 mx-auto w-48 h-48 md:w-56 md:h-56 overflow-hidden rounded-3xl shadow-lg">
                {member.image ? (
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#0A192F] to-[#1B4D3E] flex items-center justify-center">
                    <span className="text-5xl md:text-6xl font-light text-[#C5A059]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {member.initials || member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 rounded-3xl border border-[#C5A059]/30 ring-1 ring-inset ring-white/40" />
              </div>
              <h3 className="text-2xl font-light text-[#0A192F] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {member.link ? (
                  <a 
                    href={member.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-[#C5A059] transition-colors inline-flex items-center gap-1.5 group"
                    data-testid={`team-member-link-${index}`}
                  >
                    {member.name}
                    <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-[#C5A059]" />
                  </a>
                ) : (
                  member.name
                )}
              </h3>
              <p className="text-sm text-[#C5A059] font-medium tracking-wide mb-4">
                {member.title}
              </p>
              <p className="text-[#57534E] text-sm leading-relaxed max-w-sm mx-auto">
                {member.bio}
              </p>
              {member.slug && (
                <Link
                  to={`/team/${member.slug}`}
                  className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-[#C5A059] hover:text-[#0A192F] transition-colors"
                  data-testid={`team-full-bio-${member.slug}`}
                >
                  Full Bio <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Link to="/team" data-testid="meet-full-team-btn">
            <Button variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-8 py-6 text-base">
              Meet the Full Team <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>

        {/* Booking CTA — direct link to LeadConnector calendar */}
        <motion.div
          className="max-w-3xl mx-auto mt-16 md:mt-20 bg-gradient-to-br from-[#0A192F] to-[#1B4D3E] text-white p-8 md:p-12 text-center shadow-xl"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          data-testid="booking-cta-card"
        >
          <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] font-semibold mb-3">
            Take The First Step
          </p>
          <h3 className="text-3xl md:text-4xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Book a Complimentary
            <span className="block text-[#C5A059]">Discovery Call</span>
          </h3>
          <p className="text-white/70 mb-8 max-w-xl mx-auto leading-relaxed">
            A no-obligation conversation about your retirement income strategy &mdash; on Zoom, at a time that works for you.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button
              className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-10 py-7 text-lg font-semibold"
              data-testid="booking-cta-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Open the Calendar
            </Button>
          </a>
          <p className="text-white/40 text-xs mt-4">Opens in a new tab &middot; 45-minute Zoom call</p>
        </motion.div>
      </div>
    </section>
  );
};

// Our Story Section
const OurStorySection = () => {
  return (
    <section data-testid="story-section" className="py-24 md:py-32 bg-[#0A192F]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              Our Story
            </p>
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              30 Years of
              <span className="block text-[#C5A059]">Retirement Expertise</span>
            </h2>
            <div className="space-y-6 text-white/80 leading-relaxed">
              <p>
                <span className="text-white font-medium">Brandi Jo Newman</span>, founder and CEO of Ridgemont Financial, 
                was recruited off the golf course in 1996 to work with high-net-worth clients 
                at a boutique wealth management firm in Phoenix. Her background in banking, oil & gas, 
                timber, and real estate gave her a unique perspective on building and protecting wealth.
              </p>
              <p>
                Over three decades and more than <span className="text-[#C5A059] font-semibold">1,500 clients</span>, 
                she discovered a troubling pattern: too many people entering retirement were obsessed with 
                reaching the summit — maximizing their portfolio's peak value through rates of return — 
                while completely ignoring the descent.
              </p>
              <p>
                They focused on accumulation but forgot about <span className="text-white font-medium">income</span>. 
                They chased returns but overlooked <span className="text-white font-medium">income rates</span>. 
                And when it came time to actually live off their savings, many found themselves unprepared.
              </p>
              <p className="text-white font-medium">
                That's why Brandi Jo founded Ridgemont Financial — to help clients not just reach the peak, 
                but navigate the entire mountain with confidence.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-[#1B4D3E] p-10 md:p-12">
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <p className="text-5xl md:text-6xl font-light text-[#C5A059] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>30</p>
                  <p className="text-white/70 text-sm uppercase tracking-wider">Years Experience</p>
                </div>
                <div className="text-center">
                  <p className="text-5xl md:text-6xl font-light text-[#C5A059] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>1,500+</p>
                  <p className="text-white/70 text-sm uppercase tracking-wider">Clients Served</p>
                </div>
              </div>
              <Separator className="my-8 bg-white/20" />
              <blockquote className="text-white/90 italic text-lg leading-relaxed">
                "The summit isn't the goal — it's knowing how to descend safely that matters most."
              </blockquote>
              <p className="text-[#C5A059] mt-4 font-medium">— Brandi Jo Newman, Founder</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="footer-dark py-16">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {/* Brand + Contact */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <div className="flex items-center gap-3 mb-4">
              <RidgemontLogo className="w-8 h-8 text-[#C5A059]" />
              <div className="flex flex-col">
                <span className="text-2xl font-light tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Ridgemont
                </span>
                <span className="text-xs tracking-[0.25em] uppercase text-[#C5A059]">
                  Financial
                </span>
              </div>
            </div>
            <p className="text-white/50 text-sm mb-5">Your Partner in Financial Excellence</p>
            <div className="flex flex-col gap-3">
              <a 
                href="mailto:hello@ridgemontfinancial.com" 
                className="flex items-center gap-2 text-white hover:text-[#C5A059] transition-colors text-sm"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4 text-[#C5A059]" />
                <span>hello@ridgemontfinancial.com</span>
              </a>
            </div>
          </div>

          {/* Resources */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] font-semibold mb-5">
              Resources
            </p>
            <div className="flex flex-col gap-3 text-sm">
              <a 
                href="/free-book" 
                className="flex items-center gap-2 text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-free-book"
              >
                <BookOpen className="w-4 h-4 text-[#C5A059]" />
                <span>Free Retirement Book</span>
              </a>
              <a 
                href="/retirement-annuities" 
                className="flex items-center gap-2 text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-annuities"
              >
                <ShieldCheck className="w-4 h-4 text-[#C5A059]" />
                <span>Retirement Annuities</span>
              </a>
              <a 
                href="/physicians" 
                className="flex items-center gap-2 text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-physicians"
              >
                <Stethoscope className="w-4 h-4 text-[#C5A059]" />
                <span>For Physicians</span>
              </a>
              <a 
                href="/retirement-annuity-premium-bonus" 
                className="flex items-center gap-2 text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-special-programs"
              >
                <Award className="w-4 h-4 text-[#C5A059]" />
                <span>Special Programs</span>
              </a>
              <SubstackFooterLink />
            </div>
          </div>

          {/* Company */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] font-semibold mb-5">
              Company
            </p>
            <div className="flex flex-col gap-3 text-sm">
              <a 
                href="/#booking" 
                className="text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-meet-team"
              >
                Meet Our Team
              </a>
              <a 
                href="/agents" 
                className="text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-careers"
              >
                Careers
              </a>
              <a 
                href="/privacy-policy" 
                className="text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-privacy"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms-of-service" 
                className="text-white/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-terms"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-white/10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-white/40 text-xs">
          <p>&copy; {new Date().getFullYear()} Ridgemont Financial. All rights reserved.</p>
          <p className="text-white/30">Investment advisory and insurance services. Licensed in multiple states.</p>
        </div>
      </div>
    </footer>
  );
};

// Thank You Page
const ThankYouPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Simple Nav */}
      <nav className="bg-white border-b border-[#E7E5E4] py-4">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <a href="/" className="flex items-center gap-3">
            <RidgemontLogo className="w-8 h-8 text-[#1B4D3E]" />
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-light tracking-tight text-[#0A192F]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ridgemont
              </span>
              <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#1B4D3E]">
                Financial
              </span>
            </div>
          </a>
        </div>
      </nav>

      {/* Thank You Content */}
      <main className="flex-1 flex items-center justify-center py-20 px-4">
        <motion.div 
          className="max-w-2xl text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="w-20 h-20 bg-[#1B4D3E] rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Thank You for Scheduling!
          </h1>
          
          <p className="text-lg text-[#57534E] leading-relaxed mb-8">
            Your discovery appointment has been confirmed. We're excited to discuss your financial future with you. You'll receive a confirmation email shortly with all the details.
          </p>
          
          <div className="bg-white p-8 border border-[#E7E5E4] mb-8">
            <h2 className="text-xl font-medium text-[#0A192F] mb-4">What to Expect</h2>
            <ul className="text-left space-y-3 text-[#57534E]">
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2" />
                <span>A friendly, no-pressure conversation about your financial goals</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2" />
                <span>Personalized insights on retirement income strategies</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2" />
                <span>Clear next steps tailored to your unique situation</span>
              </li>
            </ul>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/">
              <Button className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 px-8 py-6 text-lg">
                Return Home
              </Button>
            </a>
            <a href="mailto:hello@ridgemontfinancial.com">
              <Button variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-8 py-6 text-lg">
                <Mail className="w-5 h-5 mr-2" />
                Email Our Team
              </Button>
            </a>
          </div>
        </motion.div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#0A192F] py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ridgemont Financial. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Physician Ribbon
const PhysicianRibbon = () => {
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-[#C5A059] text-[#0A192F] py-3 px-4 text-center border-b-4 border-[#0A192F]">
      <a 
        href="/physicians" 
        className="flex items-center justify-center gap-3 flex-wrap hover:opacity-90 transition-opacity"
        data-testid="physician-ribbon"
      >
        <span className="text-sm md:text-base font-medium">
          <span className="font-black uppercase">Attention Physicians, Engineers, Attorneys:</span>
          <span className="ml-2">Fund for just 5 years. Retire with $3M+ at 65.</span>
        </span>
        <span className="inline-flex items-center gap-2 bg-[#0A192F] text-white font-bold px-4 py-2 text-sm uppercase tracking-wide">
          See How
          <span>&rarr;</span>
        </span>
      </a>
    </div>
  );
};

// Final CTA Section - Compelling close
const FinalCTASection = () => (
  <section data-testid="final-cta-section" className="py-24 md:py-32 bg-[#0A192F] relative overflow-hidden">
    {/* Subtle background glow */}
    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C5A059]/30 rounded-full blur-[150px]" />
    </div>
    
    <div className="max-w-[900px] mx-auto px-4 md:px-8 text-center relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-6">
          Your Next Step
        </p>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          You Earned the Right to
          <span className="block text-[#C5A059]">Know Exactly What's Coming</span>
        </h2>
        <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-2xl mx-auto">
          Retirement shouldn't be a guessing game. Have a short conversation with a Ridgemont
          advisor and see what your monthly income strategy could look like.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white/5 border border-white/10 p-6">
            <p className="text-3xl font-light text-[#C5A059] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>30 yrs</p>
            <p className="text-white/50 text-sm">Helping clients retire with confidence</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-6">
            <p className="text-3xl font-light text-[#C5A059] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>1,500+</p>
            <p className="text-white/50 text-sm">Personalized retirement plans built</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-6">
            <p className="text-3xl font-light text-[#C5A059] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Free</p>
            <p className="text-white/50 text-sm">No-pressure discovery call</p>
          </div>
        </div>

        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
          <Button 
            className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-12 py-7 text-lg font-semibold"
            data-testid="final-cta-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Schedule a Call
          </Button>
        </a>
        <p className="text-white/40 text-sm mt-4">Free. Private. No strings attached.</p>
      </motion.div>
    </div>
  </section>
);

// Annuities Teaser — brief homepage block driving to /retirement-annuities
const AnnuitiesTeaser = () => (
  <section data-testid="annuities-teaser" className="py-20 md:py-24 bg-white">
    <div className="max-w-[1200px] mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }} viewport={{ once: true }}
          className="grid grid-cols-2 gap-3"
        >
          {[
            { icon: ShieldCheck, name: "Fixed (MYGA)", tag: "Certainty. Locked-in rate." },
            { icon: TrendingUp, name: "Fixed Indexed", tag: "Market-linked. Downside floor." },
            { icon: Award, name: "Immediate (SPIA)", tag: "Income starts now." },
            { icon: Calendar, name: "Deferred Income", tag: "Income turns on later." }
          ].map((t, i) => (
            <div key={i} className="bg-[#FAFAF9] border border-[#E7E5E4] p-5">
              <t.icon className="w-6 h-6 text-[#C5A059] mb-3" />
              <p className="text-sm font-semibold text-[#0A192F] leading-tight">{t.name}</p>
              <p className="text-xs text-[#57534E] mt-1 leading-snug">{t.tag}</p>
            </div>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }} viewport={{ once: true }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">
            The Retirement Toolkit
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-[#0A192F] tracking-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Annuities, explained
            <span className="block text-[#C5A059]">without the pitch.</span>
          </h2>
          <p className="text-[#57534E] leading-relaxed mb-4">
            Annuities aren't one product &mdash; they're a category. Fixed, indexed, immediate, deferred.
            Each does one thing extremely well. The right retirement usually uses a <span className="text-[#0A192F] font-medium">mix</span>.
          </p>
          <p className="text-[#57534E] leading-relaxed mb-8">
            We're independent (appointed with 12+ carriers), objective, and we tell you the tradeoffs plainly &mdash;
            so you make the decision, not us.
          </p>
          <a href="/retirement-annuities">
            <Button className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 px-8 py-6 text-base font-semibold" data-testid="annuities-teaser-cta">
              Explore Annuities
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
          <p className="text-xs text-[#57534E]/70 mt-3">Includes a free Annuity Decision Guide.</p>
        </motion.div>
      </div>
    </div>
  </section>
);


// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navigation />
      <HeroSection />
      <ValueSection />
      <AnnuitiesTeaser />
      <TeamSection />
      <ServicesSection />
      <CTABanner />
      <OurStorySection />
      <BonusPromoSection />
      <FreeBookSection />
      <SubstackInlineCTA />
      <FinalCTASection />
      <Footer />
    </>
  );
};

// Legal Page Layout
const LegalPageLayout = ({ title, children }) => {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Nav */}
      <nav className="bg-white border-b border-[#E7E5E4] py-4">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <a href="/" className="flex items-center gap-3">
            <RidgemontLogo className="w-8 h-8 text-[#1B4D3E]" />
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-light tracking-tight text-[#0A192F]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ridgemont
              </span>
              <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#1B4D3E]">
                Financial
              </span>
            </div>
          </a>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {title}
          </h1>
          <div className="prose prose-lg max-w-none text-[#57534E]">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A192F] py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ridgemont Financial. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  return (
    <LegalPageLayout title="Privacy Policy">
      <p className="text-sm text-[#C5A059] mb-6">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      
      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Introduction</h2>
        <p className="mb-4">Ridgemont Financial ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Information We Collect</h2>
        <p className="mb-4">We may collect personal information that you voluntarily provide to us when you:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Schedule a consultation through our website</li>
          <li>Contact us via phone, email, or contact forms</li>
          <li>Subscribe to our newsletter or updates</li>
          <li>Request information about our services</li>
        </ul>
        <p className="mb-4">This information may include:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Name and contact information (email address, phone number)</li>
          <li>Financial information relevant to our advisory services</li>
          <li>Employment and income information</li>
          <li>Investment goals and risk tolerance</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>How We Use Your Information</h2>
        <p className="mb-4">We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Provide, maintain, and improve our financial advisory services</li>
          <li>Communicate with you about appointments, services, and updates</li>
          <li>Respond to your inquiries and provide customer support</li>
          <li>Comply with legal and regulatory requirements</li>
          <li>Protect against fraudulent or unauthorized activity</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Information Sharing</h2>
        <p className="mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information with:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Service providers who assist in our operations (e.g., scheduling software, email services)</li>
          <li>Regulatory bodies and law enforcement when required by law</li>
          <li>Professional advisors such as lawyers and accountants</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Data Security</h2>
        <p className="mb-4">We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Your Rights</h2>
        <p className="mb-4">You have the right to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Access the personal information we hold about you</li>
          <li>Request correction of inaccurate information</li>
          <li>Request deletion of your personal information</li>
          <li>Opt-out of marketing communications</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact Us</h2>
        <p className="mb-4">If you have questions about this Privacy Policy or our privacy practices, please contact us at:</p>
        <p className="mb-2"><strong>Ridgemont Financial</strong></p>
        <p className="mb-2">Ridgemont Financial</p>
        <p className="mb-2">Email: hello@ridgemontfinancial.com</p>
      </section>
    </LegalPageLayout>
  );
};

// Terms of Service Page
const TermsOfServicePage = () => {
  return (
    <LegalPageLayout title="Terms of Service">
      <p className="text-sm text-[#C5A059] mb-6">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      
      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Agreement to Terms</h2>
        <p className="mb-4">By accessing or using the Ridgemont Financial website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Services Description</h2>
        <p className="mb-4">Ridgemont Financial provides financial planning and advisory services, including but not limited to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Retirement income planning</li>
          <li>Wealth accumulation strategies</li>
          <li>Investment advisory services</li>
          <li>Financial consultation and guidance</li>
        </ul>
        <p className="mb-4">Our website provides general information about our services and allows you to schedule consultations. The information on this website is for informational purposes only and does not constitute financial advice.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>No Financial Advice</h2>
        <p className="mb-4">The content on this website is provided for general informational purposes only and should not be construed as financial, investment, tax, or legal advice. Before making any financial decisions, you should consult with a qualified professional who can assess your individual circumstances.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>User Responsibilities</h2>
        <p className="mb-4">When using our website and services, you agree to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Provide accurate and complete information</li>
          <li>Maintain the confidentiality of any account credentials</li>
          <li>Use our services only for lawful purposes</li>
          <li>Not interfere with or disrupt our website or services</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Intellectual Property</h2>
        <p className="mb-4">All content on this website, including text, graphics, logos, and images, is the property of Ridgemont Financial and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Limitation of Liability</h2>
        <p className="mb-4">To the fullest extent permitted by law, Ridgemont Financial shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our website or services. Our total liability shall not exceed the amount you paid for our services.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Disclaimer of Warranties</h2>
        <p className="mb-4">Our website and services are provided "as is" without warranties of any kind, either express or implied. We do not guarantee that our website will be uninterrupted, secure, or error-free.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Governing Law</h2>
        <p className="mb-4">These Terms of Service shall be governed by and construed in accordance with the laws of the State of Arizona, without regard to its conflict of law provisions.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Changes to Terms</h2>
        <p className="mb-4">We reserve the right to modify these Terms of Service at any time. We will notify you of any changes by posting the new terms on this page and updating the "Last Updated" date.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact Us</h2>
        <p className="mb-4">If you have questions about these Terms of Service, please contact us at:</p>
        <p className="mb-2"><strong>Ridgemont Financial</strong></p>
        <p className="mb-2">Ridgemont Financial</p>
        <p className="mb-2">Email: hello@ridgemontfinancial.com</p>
      </section>
    </LegalPageLayout>
  );
};

// Kai-Zen Physicians Page
const KaiZenPhysiciansPage = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  
  const steps = [
    { number: "01", title: "You Contribute", description: "Fund $250,000 over 5 years ($50,000/year) into your Kai-Zen trust account." },
    { number: "02", title: "Bank Matches 3-to-1", description: "A commercial lender matches your contributions, tripling your capital at work." },
    { number: "03", title: "Tax-Free Growth", description: "Your money grows in an Indexed Universal Life policy with market upside and 0% floor protection." },
    { number: "04", title: "Bank Funds Years 6-10", description: "After your 5-year contribution, the bank continues funding at no cost to you." },
    { number: "05", title: "Tax-Free Retirement", description: "Access $3M+ in tax-free distributions starting at age 65 through age 90." }
  ];

  const comparisons = [
    { feature: "Contribution Period", traditional: "30+ years", kaizen: "5 years" },
    { feature: "Your Total Contribution", traditional: "$300,000+", kaizen: "$250,000" },
    { feature: "Leverage", traditional: "None", kaizen: "3-to-1 bank match" },
    { feature: "Market Loss Protection", traditional: "None", kaizen: "0% floor" },
    { feature: "Growth Taxation", traditional: "Taxed annually or deferred", kaizen: "Tax-free" },
    { feature: "Distributions", traditional: "Taxed at income rates", kaizen: "Tax-free" },
    { feature: "Retirement Income (Age 65-90)", traditional: "Varies, often insufficient", kaizen: "$3M+" }
  ];

  const faqs = [
    {
      question: "How does the 3-to-1 leverage work?",
      answer: "When you contribute to your Kai-Zen trust, a commercial lender matches your contribution 3-to-1. So for every $1 you put in, $3 goes to work in your policy. This dramatically increases your capital at work without increasing your out-of-pocket cost."
    },
    {
      question: "What happens after my 5 years of contributions?",
      answer: "After you complete your 5-year funding period, the bank continues to fund the policy for years 6-10 at no additional cost to you. No out-of-pocket interest, no personal guarantee required."
    },
    {
      question: "How is this tax-free?",
      answer: "Kai-Zen uses an Indexed Universal Life (IUL) insurance policy structure. Growth inside the policy is tax-deferred, and distributions are taken as policy loans which are tax-free. You also receive tax-free death benefits for your beneficiaries."
    },
    {
      question: "What does '0% floor protection' mean?",
      answer: "Your policy is linked to market indexes like the S&P 500, so you can participate in upside growth. The policy is also structured with a 0% floor, which is designed so that index-linked credits don't drop below zero in a down year. Policy charges and other contractual provisions still apply, and specific limitations vary by carrier."
    },
    {
      question: "What are the requirements to participate?",
      answer: "Kai-Zen is designed for high-income professionals earning $200K+ annually. The minimum contribution is $21,350/year (includes $1,350 annual trust fees). You must be insurable and a participant trust will be established as part of the strategy."
    },
    {
      question: "What if I need access to funds before retirement?",
      answer: "Kai-Zen includes living benefits. If you experience a chronic or terminal illness, you can access policy benefits. You can also access cash values for unexpected life events, though early access may impact your long-term retirement projections."
    }
  ];

  const openLeadForm = () => setIsLeadModalOpen(true);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Navigation */}
      <nav className="bg-[#0A192F] py-4">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <RidgemontLogo className="w-8 h-8 text-[#C5A059]" />
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-light tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ridgemont
              </span>
              <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#C5A059]">
                Financial
              </span>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <SubstackHeaderButton />
            <a href="mailto:hello@ridgemontfinancial.com" className="flex items-center gap-2 text-white hover:text-[#C5A059] transition-colors">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">hello@ridgemontfinancial.com</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-[#0A192F] py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-6">
              Exclusive Retirement Strategy for Physicians
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-[1.1] tracking-tight mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              $250K Over 5 Years.
              <span className="block text-[#C5A059]">$3M+ at 65.*</span>
            </h1>
            <p className="text-sm text-white/50 mb-6">*Based on a 40-year-old participant</p>
            <p className="text-xl text-white/70 leading-relaxed mb-10 max-w-2xl mx-auto">
              Stop contributing for 30 years and still coming up short. The Kai-Zen® strategy uses 3-to-1 bank leverage to put dramatically more capital to work—with tax-free growth and tax-free distributions.
            </p>
            <Button 
              onClick={openLeadForm}
              className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-10 py-7 text-lg font-semibold"
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              Run Your Own Numbers
            </Button>
          </motion.div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              You Built a High Income.<br />
              <span className="text-[#1B4D3E]">Don't Retire With a High Tax Bill.</span>
            </h2>
            <p className="text-lg text-[#57534E] leading-relaxed mb-8">
              You've spent your career in one of the highest tax brackets in the country. Most retirement accounts hand you a tax bill when you need the money most. Research shows <strong>74% of financial success in retirement comes from how much capital is working for you</strong>—not your returns, not your fund selection. Your capital.
            </p>
            <p className="text-lg text-[#57534E] leading-relaxed">
              Most physicians spend decades optimizing the 26%. Kai-Zen® fixes the 74%.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              The Process
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              How Kai-Zen® Works
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className="bg-white p-6 border border-[#E7E5E4] relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <span className="text-5xl font-light text-[#C5A059]/30 absolute top-4 right-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {step.number}
                </span>
                <h3 className="text-lg font-medium text-[#0A192F] mb-2 mt-8">{step.title}</h3>
                <p className="text-sm text-[#57534E]">{step.description}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA after steps */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-[#57534E] mb-4">Ready to see what this looks like with your numbers?</p>
            <Button 
              onClick={openLeadForm}
              className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-8 py-6 text-lg font-semibold"
              data-testid="physicians-steps-cta"
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              Run Your Own Numbers
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 md:py-28 bg-[#0A192F]">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              The Difference
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Traditional Retirement vs. Kai-Zen®
            </h2>
          </motion.div>

          <motion.div
            className="bg-white/5 border border-white/10 overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-3 bg-white/10">
              <div className="p-4 text-white/60 text-sm font-medium">Feature</div>
              <div className="p-4 text-white/60 text-sm font-medium text-center">Traditional</div>
              <div className="p-4 text-[#C5A059] text-sm font-medium text-center">Kai-Zen®</div>
            </div>
            {comparisons.map((row, index) => (
              <div key={index} className="grid grid-cols-3 border-t border-white/10">
                <div className="p-4 text-white text-sm">{row.feature}</div>
                <div className="p-4 text-white/50 text-sm text-center">{row.traditional}</div>
                <div className="p-4 text-[#C5A059] text-sm text-center font-medium">{row.kaizen}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 md:py-28 bg-[#1B4D3E]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              The Advantages
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Why Physicians Choose Kai-Zen®
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: "3-to-1 Leverage", description: "Bank matches your contributions, tripling your capital at work from day one." },
              { icon: ShieldCheck, title: "0% Floor Protection", description: "Designed to participate in market-linked gains with a 0% floor that helps limit losses from market downturns." },
              { icon: Award, title: "Tax-Free Growth", description: "Your money compounds without annual tax drag eating into your returns." },
              { icon: Target, title: "Tax-Free Distributions", description: "Access your retirement income without handing a chunk to the IRS." },
              { icon: Users, title: "Living Benefits", description: "Chronic and terminal illness riders provide protection if life takes an unexpected turn." },
              { icon: Mountain, title: "Legacy Planning", description: "Enhanced death benefits pass tax-free to your beneficiaries." }
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="bg-white/10 backdrop-blur-sm p-8 border border-white/20"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <benefit.icon className="w-10 h-10 text-[#C5A059] mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">{benefit.title}</h3>
                <p className="text-white/70">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA after benefits */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Button 
              onClick={openLeadForm}
              className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-8 py-6 text-lg font-semibold"
              data-testid="physicians-benefits-cta"
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              Get Your Free Illustration
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[800px] mx-auto px-4 md:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">
              Common Questions
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="border border-[#E7E5E4] p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <h3 className="text-lg font-medium text-[#0A192F] mb-3">{faq.question}</h3>
                <p className="text-[#57534E] leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Inline Lead Capture Form */}
      <InlineLeadSection />

      {/* Footer */}
      <footer className="bg-[#0A192F] border-t border-white/10 py-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-3">
              <RidgemontLogo className="w-6 h-6 text-[#C5A059]" />
              <span className="text-white font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ridgemont Financial</span>
            </div>
            <div className="flex items-center gap-6 text-white/60 text-sm">
              <a href="/" className="hover:text-[#C5A059] transition-colors">Home</a>
              <a href="/privacy-policy" className="hover:text-[#C5A059] transition-colors">Privacy Policy</a>
              <a href="/terms-of-service" className="hover:text-[#C5A059] transition-colors">Terms of Service</a>
              <a href="https://substack.com/@retirewithcertainty" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6719] text-[#FF6719] transition-colors" data-testid="physicians-substack-footer">Newsletter</a>
            </div>
          </div>
          
          <Separator className="bg-white/10 mb-8" />
          
          <div className="text-white/30 text-xs leading-relaxed">
            <p>
              <strong>Assumptions Used for Kai-Zen® and Alternative Choices:</strong> Allocated to S&P Annual PtP No Bonus No Additional Charge (Illustrated Growth Rate: 6.65%). IUL insurance policy distributions begin at the later of age 65 or after the lender loan has been repaid (typically in the 15th policy year) and continue through age 90. Long Term Capital Gains Rate of 20%. Income tax rate of 37%. Participation in Kai-Zen requires a minimum contribution amount of $21,350 annually, which includes the annual trust fees of $1,350 and a portion of the IUL insurance policy premium. Kai-Zen also requires a participant trust to be established as part of the strategy. The total IUL insurance premium is paid by the trust to the life insurer once you and the commercial lender contribute sufficient funds to pay the IUL insurance policy premium to the trust. Guarantees are backed by the financial strength and claims-paying ability of the issuing insurance company. No investments are being offered.
            </p>
          </div>
          
          <p className="text-white/40 text-xs mt-6 text-center">&copy; {new Date().getFullYear()} Ridgemont Financial. All rights reserved.</p>
        </div>
      </footer>
      
      {/* Lead Capture Modal */}
      <LeadCaptureModal 
        isOpen={isLeadModalOpen} 
        onClose={() => setIsLeadModalOpen(false)} 
      />
    </div>
  );
};

// Main App Component
import AgentIntakePage from "./AgentIntake";
import AgentsPage from "./AgentsPage";
import ProductsPage from "./ProductsPage";
import FreeBookPage from "./FreeBookPage";
import AnnuitiesPage from "./AnnuitiesPage";
import WorkbookPage from "./WorkbookPage";
import TeamPage from "./TeamPage";
import AgentPage from "./AgentPage";

function ChatWidgetGate() {
  const location = useLocation();
  const excluded = ["/agents", "/agent-intake"];
  if (excluded.includes(location.pathname)) return null;
  return <ChatWidget />;
}

function App() {
  return (
    <BrowserRouter>
      <div className="App" data-testid="app-container">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/physicians" element={<KaiZenPhysiciansPage />} />
          <Route path="/agent-intake" element={<AgentIntakePage />} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/retirement-annuity-premium-bonus" element={<ProductsPage />} />
          <Route path="/free-book" element={<FreeBookPage />} />
          <Route path="/retirement-annuities" element={<AnnuitiesPage />} />
          <Route path="/retirement-workbook" element={<WorkbookPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/:slug" element={<AgentPage />} />
        </Routes>
        <ChatWidgetGate />
      </div>
    </BrowserRouter>
  );
}

export default App;
